import {
	format,
	isThisYear,
	isToday,
	isTomorrow,
	isYesterday,
	type Locale,
} from 'date-fns';
import { type useTranslation } from '@/composables';

/**
 * The payload passed through the pipeline of formatters.
 */
export type FormatterPayload = {
	/** Date format in `date-fns` format. */
	dateFormat: string;
	/** Time format in `date-fns` format. */
	timeFormat: string;
	/** The date that is being formatted. */
	date: Date;
	/** Translation function. */
	t: ReturnType<typeof useTranslation>['t'];
	/** The currently active date-fns locale */
	dateFnsLocale: Locale;
};

/**
 * Each formatter function accepts a `FormatterPayload` and returns a modified
 * version of it.
 *
 * For example, it can replace the date format string with "Today".
 */
type Formatter = (payload: FormatterPayload) => FormatterPayload;

/**
 * All of these formatter methods have been built with the purpose of using them with the flow() function.
 * But flow() only allows functions with arity of 1.
 * This function let's use change the FormatterPayload within the flow() calls.
 */
export const override = (override: Partial<FormatterPayload>): Formatter => {
	return (payload) => ({
		...payload,
		...override,
	});
};

export const removeYear: Formatter = (payload) => ({
	...payload,
	dateFormat: payload.dateFormat.replace(/[-./]?yyyy[-./]?/, ''),
});

/**
 * Remove the year from the format if the year is the same as the current year.
 *
 * NOTE: This function is NOT pure because `isThisYear` uses `Date.now()`
 * internally. This can easily be mocked in tests though!
 */
export const removeYearIfCurrent: Formatter = (payload) =>
	isThisYear(payload.date) ? removeYear(payload) : payload;

/**
 * Add the weekday to the front of the date format.
 */
export const addWeekdayToFront: Formatter = (payload) => ({
	...payload,
	dateFormat: `E, ${payload.dateFormat}`,
});

/**
 * If the given date is today or yesterday, then replace the date with the
 * word.
 *
 * NOTE: This function is meant to be called AFTER date-fns has formatted the
 * date. This is because "today", "yesterday" or other results can include
 * date-fns formatting tokens. E.g., "today" can become "toda2021". So, make
 * sure to call this AFTER calling date-fns `format()`.
 */
export const replaceDateWithToday: Formatter = (payload) => {
	let dateFormat = payload.dateFormat;
	if (isToday(payload.date)) {
		dateFormat = payload.t('today');
	} else if (isYesterday(payload.date)) {
		dateFormat = payload.t('yesterday');
	} else if (isTomorrow(payload.date)) {
		dateFormat = payload.t('tomorrow');
	}

	return {
		...payload,
		dateFormat,
	};
};

/**
 * Format the date with the formats from the payload.
 */
export const applyDateFnsFormat: Formatter = (payload) => ({
	...payload,
	dateFormat: format(payload.date, payload.dateFormat, {
		locale: payload.dateFnsLocale,
	}),
	timeFormat: format(payload.date, payload.timeFormat, {
		locale: payload.dateFnsLocale,
	}),
});

/**
 * An "extractor" should convert the given payload into a string.
 *
 * For example, it can concatenate the date and time.
 */
type Extractor = (payload: FormatterPayload) => string;

/**
 * Concatenate the date and time from the payload.
 */
export const extractDateAndTime: Extractor = (payload) =>
	`${payload.dateFormat} ${payload.timeFormat}`;

/**
 * Only return the date from the payload.
 */
export const extractDate: Extractor = (payload) => payload.dateFormat;

/**
 * Only return the time from the payload.
 */
export const extractTime: Extractor = (payload) => payload.timeFormat;

/**
 * Convert the user's saved date format preference to a format for `date-fns`.
 *
 * The user date format is saved with tokens that differ from the tokens that
 * `date-fns` expects. "%d" is the day, "%m" is the month, and "%Y" signifies
 * the year. We need to convert that format into a format that `date-fns` can
 * use for formatting.
 */
export const convertUserDateFormatToDateFns = (dateFormat: string) =>
	dateFormat.replace('%d', 'dd').replace('%m', 'MM').replace('%Y', 'yyyy');

/**
 * Conver the user's saved time format preference to a format for `date-fns`.
 *
 * The time format can either be "12" for 12-hour format or "24" for 24-hour
 * format.
 */
export const convertUserTimeFormatToDateFns = (timeFormat: string) =>
	timeFormat === '24' ? 'HH:mm' : 'hh:mm a';

export const dateToMysqlString = (date: Date, withTime = false) => {
	return format(date, withTime ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd');
};
